'use client';

import { LoadingDark } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ErrorAndRetry } from '../../common/components/ErrorAndRetry';
import { useMyCurationsInfiniteQuery } from '../../common/hooks/useMyCurationsInfiniteQuery';
import { EmptyMyShopCurationList } from './EmptyMyShopCurationList';
import { MyShopCurationItem } from './MyShopCurationItem';

export type MyShopCurationListProps = {
  /** 무신사 탭 연동 시에만 전달 — 서버 목록 필터 */
  apiCurationType?: 'GOODS' | 'SNAP2';
  /** 최상단 노출(pinned)할 큐레이션 ID */
  curationId?: string;
};

export const MyShopCurationList = ({ apiCurationType, curationId }: MyShopCurationListProps) => {
  const { products, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, isError, refetch } =
    useMyCurationsInfiniteQuery({ size: 5, type: apiCurationType, curationId });
  const { isWebview, service } = useDeviceInfo();

  const visibleProducts = useMemo(
    () => products.filter((c) => c.type !== 'SNAP2' || service === SERVICE.MUSINSA),
    [products, service],
  );

  const [pageCache, setPageCache] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    setPageCache(new Map());
  }, [apiCurationType, curationId]);

  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderItem = useCallback(
    (index: number) => {
      const curation = visibleProducts[index];
      if (!curation) return null;

      return (
        <MyShopCurationItem
          key={curation.curationId}
          item={curation}
          pageCache={pageCache}
          setPageCache={setPageCache}
          curationIndex={index}
        />
      );
    },
    [visibleProducts, pageCache, setPageCache],
  );

  const computeItemKey = useCallback(
    (index: number) => {
      const curation = visibleProducts[index];
      if (!curation) return `item-${index}`;
      const goodsStates = curation.goodsItems
        .map((goods) => `${goods.goodsNo}-${goods.isLiked ? '1' : '0'}`)
        .join('_');
      const snapIds = curation.snapItems.map((s) => s.id).join('_');
      return `${curation.curationId}-${curation.type ?? 'GOODS'}-${goodsStates}-${snapIds}`;
    },
    [visibleProducts],
  );

  if (isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorAndRetry
        className="flex flex-1 items-center justify-center p-4"
        onRetry={() => refetch()}
      />
    );
  }

  if (visibleProducts.length === 0) {
    return <EmptyMyShopCurationList />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <Virtuoso
        style={{ height: '100%' }}
        useWindowScroll
        totalCount={visibleProducts.length}
        itemContent={renderItem}
        computeItemKey={computeItemKey}
        endReached={onEndReached}
        overscan={2}
        increaseViewportBy={{ top: 500, bottom: 500 }}
      />
    </div>
  );
};
